/*
 *   File : employees.js
 *   Author : https://evoqins.com
 *   Description : sales specialist page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Custom components
import { DataTableComponent } from '../../../../Component/DataTable';
import { CustomSearchInput } from '../../../../Component/FormElements';
import { SummaryCard } from '../../../../Component/Cards';
import { Icon } from '../../../../Component/Icon';
import { APIService } from '../../../../Service';
import { Loader } from '../../../../Component/Others';
import { EmptyScreen } from '../../../../Component/EmptyComponent';
import { DownloadButton } from '../../../../Component/Buttons';
import { toast } from 'react-toastify';

const ITEMS_PER_PAGE = 20;

const PAGE_SORT_FIELDS = [
    'customer_id',
    'user_id',
    'customer_name',
    'joined_on',
    'email',
    'contact_no',
    'reward',
    'total_sales',
    'revenue',
    'store_name',
];

const Employee = (props) => {

    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);

    const tableRef = useRef(null);
    const navigate = useNavigate();

    const [searchValue, setSearchValue] = useState("");
    const [registeredMemberData, setRegisteredMemberData] = useState({});
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const [downloadLoader, setDownloadLoader] = useState(false);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _registeredMembers();
    }, [searchValue, currentPage]);

    // handle search registered members 
    const _handleSearchMembers = (value) => {
        setSearchValue(value);
        setLoading(true);
        setCurrentPage(1);
    };

    // handle table page change 
    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        setLoading(true);
        if (tableRef.current) {
            tableRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !PAGE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoading(true);

        // Only skip the list useEffect when page will actually change.
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _registeredMembers(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    // handle navigate detail page 
    const _handleNavigateDetail = (id) => {
        navigate("/sales-specialist/detail", {
            state: {
                customer_id: id,
                tabIndex: 1
            }
        });
    };

    // registered members table 
    const TABLE_COLUMNS = [
        {
            name: 'User. ID',
            id: 'user_id',
            sortField: 'user_id',
            selector: row => row.user_id === null ? '-' : row.user_id,
            width: "120px",
            sortable: true
        },
        {
            name: 'Name',
            id: 'customer_name',
            sortField: 'customer_name',
            selector: row => row.customer_name,
            width: "180px",
            sortable: true
        },
        {
            name: 'Joined on',
            id: 'joined_on',
            sortField: 'joined_on',
            selector: row => row.joined_on,
            cell: row => <>{row.joined_on}</>,
            format: row => row.joined_on,
            width: "140px",
            sortable: true
        },
        {
            name: 'Email',
            id: 'email',
            sortField: 'email',
            selector: row => row.email || "-",
            width: "200px",
            sortable: true,
            cell: (row) => row.email ?
                <a href={`mailto:${row.email}`} className='e-text-rich-black text-decoration-hover-underline text-decoration-none cursor-pointer'>
                    {row.email}
                </a>
                :
                '-'
        },
        {
            name: 'Phone no.',
            id: 'contact_no',
            sortField: 'contact_no',
            selector: row => row.contact_no || "-",
            width: "160px",
            sortable: true,
            cell: (row) => row.contact_no ?
                <a href={`tel:${row.contact_no}`} className='e-text-rich-black text-decoration-hover-underline text-decoration-none cursor-pointer'>
                    {row.contact_no}
                </a>
                :
                '-'
        },
        {
            name: PROFILE_DATA.country === "Egypt" ? 'Volume (Units)' : 'Volume (Ltr)',
            id: 'total_sales',
            sortField: 'total_sales',
            selector: row => row.total_sales,
            width: "140px",
            sortable: true
        },
        {
            name: "Points",
            id: 'reward',
            sortField: 'reward',
            selector: row => row.reward,
            width: "140px",
            sortable: true
        },
        // {
        //     name: "Revenue generated",
        //     selector: row => row.revenue,
        //     width: "160px"
        // }

    ];

    // API - Get store detail data
    const _registeredMembers = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        const effective_sort_by = sort_by && PAGE_SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'CA/store/registered-member-list';
        let data = {
            "store_id": props.storeId,
            "query": searchValue,
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setRegisteredMemberData(response.data);
            } else {
                setRegisteredMemberData({});
            }
            setLoading(false);
        });
    };

    // API - Get store performance summary download
    const _download = () => {
        setDownloadLoader(true);
        const effective_sort_by = sortField && PAGE_SORT_FIELDS.includes(sortField) ? sortField : null;
        const effective_sort_order = effective_sort_by ? sortDirection : null;

        let is_post = true;
        let url = 'CA/store/registered-member-list/download';
        let data = {
            "store_id": props.storeId,
            "query": searchValue || null,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.data.download_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };

    return (
        <>
            {/* Summary  */}
            <div className='e-admin-box-shadow p-4 e-bg-white e-border-radius-16'>
                <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                    Summary
                </h4>
                <div className='row row-cols-xl-4 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-1 row-gap-3 padding-10px-top'>

                    <div className='col'>
                        <SummaryCard title="No. of sales specialists"
                            loader={props.loader}
                            quantity={props.summaryData.sales_specialist} />
                    </div>
                    <div className='col'>
                        <SummaryCard title={PROFILE_DATA.country === "Egypt" ? "Total sales in units" : "Total sales in litres"}
                            loader={props.loader}
                            quantity={props.summaryData.average_sale} />
                    </div>
                    <div className='col'>
                        <SummaryCard title={PROFILE_DATA.country === "Egypt" ? "Premium sales in litres" : "Premium sales in units"}
                            loader={props.loader}
                            quantity={"-"} />
                    </div>
                    <div className='col'>
                        <SummaryCard title={"Total ponts"}
                            loader={props.loader}
                            quantity={props.summaryData.points_earned} />
                    </div>
                </div>
            </div>

            {/* Registered members table */}
            <div ref={tableRef} className='margin-20px-top e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                <div className='row px-4'>
                    <div className=' col-lg-5 col-md-6 col-sm-12'>
                        <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-600 line-height-28px' >
                            All registered members
                        </h4>
                    </div>
                    <div className='col-lg-7 col-md-6 col-sm-8 col-12 pt-xl-0 pt-lg-0 pt-md-0 pt-2 text-end'>
                        <div className='d-flex align-items-center gap-4 justify-content-end'>
                            <DownloadButton loader={downloadLoader}
                                disabled={loading || Object.keys(registeredMemberData).length === 0 || registeredMemberData?.data?.length === 0}
                                onClick={_download} />

                            <CustomSearchInput placeholder="Search by name"
                                type="text"
                                require
                                value={searchValue}
                                className="w-400px padding-8px-tb py-2 "
                                labelStyle="e-font-weight-500"
                                iconClass="top-10px"
                                handleChange={(e) => _handleSearchMembers(e)}
                                handleClear={() => {
                                    setSearchValue("");
                                    setSortField(null);
                                    setSortDirection(null);
                                    setLoading(true);
                                }} />
                        </div>
                    </div>
                </div>

                {loading ?
                    // loader 
                    <Loader />
                    :
                    <>
                        {/* registered members data table */}
                        {Object.keys(registeredMemberData)?.length !== 0 && registeredMemberData?.data?.length !== 0 ?
                            <div className='e-members-detail-table'>
                                <DataTableComponent
                                    key={`store-members-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                    columns={TABLE_COLUMNS}
                                    data={registeredMemberData.data}
                                    pagination={true}
                                    paginationServer={true}
                                    paginationPerPage={ITEMS_PER_PAGE}
                                    paginationDefaultPage={currentPage}
                                    paginationTotalRows={registeredMemberData.total_count}
                                    paginationResetDefaultPage={false}
                                    sortServer={true}
                                    defaultSortFieldId={sortField}
                                    defaultSortAsc={sortDirection !== 'desc'}
                                    onSort={_handleSort}
                                    rowClick={(row) => _handleNavigateDetail(row.customer_id)}
                                    onChangePage={_handlePageChange} />
                            </div>
                            :
                            // empty screen
                            <div className='h-100vh pt-5 mt-5'>
                                <EmptyScreen message="No data found" />
                            </div>
                        }
                    </>

                }
            </div>
        </>
    )
}

export default Employee;
